import { Component, OnInit, ViewChild } from '@angular/core';
import { CustomerManagementService } from '../services/customer-management/customer-management.service';
import { Customer, CustomerManagementStats, ExportRetryData, ForecastedUsageApiResponse, IncidentData, IPieChartItem, TenantUsageData } from '../app.types';
import { UsageAmountsService } from '../services/usage/usage-amounts.service';
import { map, switchMap } from 'rxjs/operators';
import { convert, convertToPercent } from 'src/util/conversion-helpers';
import { nullCheckWrap } from 'src/util/function-helper';
import { convertToUtcDate, formatDate } from 'src/util/date-helpers';
import { TenancyConfigurationService } from '../services/tenancy-configuration.service';
import { ICountCard } from './count-card/count-card.component';
import { combineLatest } from 'rxjs';
import { ExportService, CsvTable } from '../services/export/export.service';
import { IncidentsCreatedChartComponent } from './incidents-created-chart/incidents-created-chart.component';
import { IncidentsSharedChartComponent } from './incidents-shared-chart/incidents-shared-chart.component';
import { DownloadsChartComponent } from './downloads-chart/downloads-chart.component';
import { StreamingGraphComponent } from './streaming-graph/streaming-graph.component';
import { RedactionGraphComponent } from './redaction-graph/redaction-graph.component';
import { UploadStatusesChartComponent } from './upload-statuses-chart/upload-statuses-chart.component';
import { AuditLogService } from '../services/audit-log/audit-log.service';

@Component({
    selector: 'customer-usage',
    templateUrl: './customer-usage.component.html',
    styleUrls: ['./customer-usage.component.scss'],
    standalone: false
})
export class CustomerUsageComponent implements OnInit {
  @ViewChild(IncidentsCreatedChartComponent) incidentsCreatedChart: IncidentsCreatedChartComponent;
  @ViewChild(IncidentsSharedChartComponent) incidentsSharedChart: IncidentsSharedChartComponent;
  @ViewChild(DownloadsChartComponent) downloadsChart: DownloadsChartComponent;
  @ViewChild(StreamingGraphComponent) streamingGraph: StreamingGraphComponent;
  @ViewChild(RedactionGraphComponent) redactionGraph: RedactionGraphComponent;
  @ViewChild(UploadStatusesChartComponent) uploadStatusesChart: UploadStatusesChartComponent;

  startOfMonth: Date = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1));
  startOfInitialData: Date = new Date(Date.UTC(new Date().getFullYear()-1, new Date().getMonth() + 1, 1));
  retentionPeriodInDays = 0;

  usersData?: { [group: string]: IPieChartItem[] };

  customers: Customer[] = [];
  customersHeadlineStats: CustomerManagementStats[] = [];

  selectedHeadlineStats: CustomerManagementStats | null = null;

  incidentsCreatedMonthlyCard: ICountCard;
  incidentsSharedMonthlyCard: ICountCard;
  
  totalIncidentsCard: ICountCard;
  activeIncidentsCard: ICountCard;
  totalIncidentsSharedCard: ICountCard;
  
  retryAttemptsCard: ICountCard;
  successfulRetriesCard: ICountCard;
  failedRetriesCard: ICountCard;
  
  yearlyStreamingAllowanceCard: ICountCard;
  monthlyStreamingCard: ICountCard;
  
  monthlyRedactionRequestsCard: ICountCard;
  monthlyRedactionMinutesCard: ICountCard;
  monthlyAudioRedactionRequestsCard: ICountCard;

  latestIncidentData: IncidentData;
  initialTenantData: TenantUsageData;

  loggedInUsersCount = 0;
  totalUserGroupCount = 0;

  uploadsChartMin = 425;
  uploadsChartMax = 720;

  _selectedCustomer: Customer | null = null;

  public set selectedCustomer(v: Customer | null) {
    this._selectedCustomer = v;
    this.selectedHeadlineStats = this.customersHeadlineStats.find(stats => stats.tenantId === v?.tenantId) || null;

    this.yearlyStreamingAllowanceCard = {
      label: 'Total Live Video Minutes (Yearly Allowance)',
      count: this._selectedCustomer?.subscriptionDetails?.liveVideoLimit * 12 || 0
    };

    this.tenancyConfigService.getTenancyConfiguration(v?.tenantId)
      .pipe(
        switchMap(config => {
          const endOfFirstRetention = v?.subscriptionStartDate && config?.incidentRetentionPeriodInDays ? 
            new Date(new Date(v.subscriptionStartDate).getTime() + config?.incidentRetentionPeriodInDays * 24 * 60 * 60 * 1000) : null;
          this.retentionPeriodInDays = config?.incidentRetentionPeriodInDays || 0;
          return this.usageService.getForecast(v?.tenantId || '', this.startOfInitialData.toISOString(), 12, endOfFirstRetention)
            .pipe(map(forecast => ({forecast, endOfFirstRetention})));
        })
    ).subscribe(({forecast, endOfFirstRetention}) => {
        this.initialTenantData = {
          usage: forecast, 
          endOfFirstRetention: endOfFirstRetention,
          customer: v!
        };
        const today = new Date();
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

        const currentMonthData = Object.entries(forecast).find(([date]) => new Date(date.replaceAll('/', '-')).getMonth() === today.getMonth());
        const prevMonthData = Object.entries(forecast).find(([date]) => new Date(date.replaceAll('/', '-')).getMonth() === lastMonth.getMonth());

        console.log('Current Month Data', currentMonthData);
        console.log('Previous Month Data', prevMonthData);

        if(!currentMonthData || !prevMonthData) {
          return;
        }

        this.latestIncidentData = currentMonthData[1].actualUsage;

        this.incidentsCreatedMonthlyCard = this.createCardData(
          'Incidents Created',
          prevMonthData[1].actualUsage.incidentsCreatedLastHourCount || 0,
          this.latestIncidentData.incidentsCreatedLastHourCount || 0
        );

        this.incidentsSharedMonthlyCard = this.createCardData(
          'Incidents Shared',
          prevMonthData[1].actualUsage.incidentSharesInLastHourCount || 0,
          this.latestIncidentData.incidentSharesInLastHourCount || 0
        );

        this.totalIncidentsCard = {
          label: 'Total Incidents',
          count: this.latestIncidentData.totalIncidents || 0
        };

        this.activeIncidentsCard = {
          label: 'Active Incidents',
          count: this.latestIncidentData.activeIncidents || 0
        };

        this.totalIncidentsSharedCard = {
          label: 'Total Incidents Shared',
          count: this.latestIncidentData.totalIncidentsShared || 0
        };

        this.monthlyStreamingCard = {
          label: 'Live Video Watched in Minutes (Current Month)',
          count: this.latestIncidentData.minutesStreamedInTimeFrame || 0
        };

        this.monthlyRedactionRequestsCard = {
          label: 'Redaction Requests',
          count: this.latestIncidentData.redactionRequestsLastHourCount || 0
        };

        this.monthlyRedactionMinutesCard = {
          label: 'Redacted Videos (Mins)',
          count: Math.floor((this.latestIncidentData.redactionSecondsLastHourCount || 0) / 60)
        };

        this.monthlyAudioRedactionRequestsCard = {
          label: 'Redacted Audio Requests',
          count: this.latestIncidentData.audioRedactionRequestsInTimeFrame || 0
        };
      });

      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      combineLatest([
        this.usageService.getActiveUsersCount(v?.tenantId, 
          convertToUtcDate(thirtyDaysAgo).toISOString(), 
          convertToUtcDate(today).toISOString()),
        this.usageService.getActiveUsersCount(v?.tenantId, 
          convertToUtcDate(sevenDaysAgo).toISOString(), 
          convertToUtcDate(today).toISOString()),
        this.usageService.getEvidenceLockerStatsSingle(v?.tenantId, 
          convertToUtcDate(v?.subscriptionStartDate).toISOString())
      ]).subscribe(([thirtyToTodayActive, sevenDaysToTodayActive, stats]) => {
        console.log('Active Users Stats', {thirtyToTodayActive, sevenDaysToTodayActive, stats});
        this.usersData = {
          'Active Users': [
            { name: '7 Days and Under', value: sevenDaysToTodayActive || 0, color: "#14AE5C" },
          ],
          'Inactive Users': [
            { name: 'Over 7 Days', value:(thirtyToTodayActive || 0) - (sevenDaysToTodayActive || 0), color: "#F5CF45" },
            { name: 'Over 30 Days', value:(stats.totalUsers || 0) - (thirtyToTodayActive || 0), color: "#FF6861" },
          ]
        };

        this.loggedInUsersCount = stats.loggedInUsers || 0;
        this.totalUserGroupCount = stats.totalUserGroups || 0;
      });

      this.usageService.getRetryExportStats(v?.tenantId, this.startOfMonth.toISOString(), new Date().toISOString()).subscribe((res: ExportRetryData) => {
        this.retryAttemptsCard = {
          label: 'Retry Attempts in the Current Month',
          count: res.totalRetryAttempts || 0
        };

        this.successfulRetriesCard = {
          label: 'Successful Retries',
          count: res.successfulRetries || 0
        };
        
        this.failedRetriesCard = {
          label: 'Failed Retries',
          count: res.failedRetries || 0
        };
      });
  }
  
  public get selectedCustomer(): Customer | null {
    return this._selectedCustomer;
  }

  constructor(private customerService: CustomerManagementService, private usageService: UsageAmountsService,
    private tenancyConfigService: TenancyConfigurationService, private exportService: ExportService,
    private auditLogService: AuditLogService
  ) {}

  ngOnInit(): void {
    this.usageService.getMonthlyAmounts().pipe(
      switchMap((stats) => {
        console.log(stats);
        this.customersHeadlineStats = stats;
        return this.customerService.getCustomers({});
      })
    ).subscribe(
      (res: any) => {
        if(res?.body?.results){
          this.customers = res.body.results;
          this.selectedCustomer = this.customers.length > 0 ? this.customers[0] : null;
          this.selectedHeadlineStats = this.customersHeadlineStats.find(stats => stats.tenantId === this.selectedCustomer?.tenantId) || null;
        }
      }
    );
  }

  getCustomerLabel(customer: Customer): string {
    return customer?.name;
  }

  convertBytesToGigabytes(bytes: number): number {
    return convert('bytes', 'gigabytes', bytes);
  }

  formatDate(date: Date): string {
    return nullCheckWrap(formatDate).call(this, date);
  }

  createCardData(label: string, prev: number, current: number): ICountCard {
    return {
      label,
      count: current,
      percentageProgress: {
        percentage: convertToPercent(current - prev, prev),
        decreasing: current < prev,
        color: current < prev ? "#22985F" : "#D31D35",
      }
    };
  }

  generateYearDateRangeString(startDate: Date, defaultStr: string): string {
    if (!startDate) {return defaultStr;}
    const options = { month: 'long', year: 'numeric' } as const;
    const startStr = startDate.toLocaleDateString('en-GB', options);
    const endStr = new Date(startDate.getFullYear(), startDate.getMonth() + 12, 1).toLocaleDateString('en-GB', options);
    return `${startStr} - ${endStr}`;
  }

  exportUsageData(): void {
    const customer = this.selectedCustomer;
    if (!customer) {return;}

    const currentMonth = new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    const incidentsCreatedDateRangeStr = this.generateYearDateRangeString(this.incidentsCreatedChart?.selectedDate, currentMonth);
    const incidentsSharedDateRangeStr = this.generateYearDateRangeString(this.incidentsSharedChart?.selectedDate, currentMonth);
    const streamingDateRangeStr = this.generateYearDateRangeString(this.streamingGraph?.selectedDate, currentMonth);
    const videoRedactionDateRangeStr = this.generateYearDateRangeString(this.redactionGraph?.selectedDate, currentMonth);

    const tables: CsvTable[] = [
      {
        title: `Customer Overview - ${customer.name}`,
        data: [
          { Metric: 'Vehicles Configured', Value: `${customer.assetCount ?? 0} of ${customer.subscriptionDetails?.assetLimit ?? 0}` },
          { Metric: 'Current Storage', Value: `${this.convertBytesToGigabytes(this.selectedHeadlineStats?.blobStorageUsed)} GB of ${customer.subscriptionDetails?.storageLimit ?? 0} TB` },
          { Metric: 'Subscription Start Date', Value: customer.subscriptionStartDate ? this.formatDate(customer.subscriptionStartDate) : '' },
          { Metric: 'Subscription End Date', Value: customer.subscriptionEndDate ? this.formatDate(customer.subscriptionEndDate) : '' },
          { Metric: 'Tenancy Creation Date', Value: customer.createdDate ? this.formatDate(customer.createdDate) : '' },
          { Metric: 'Retention Period', Value: `${this.retentionPeriodInDays} days` },
        ]
      },
      {
        title: `Incidents (${currentMonth})`,
        data: [
          { Metric: this.incidentsCreatedMonthlyCard?.label ?? 'Incidents Created', Value: this.incidentsCreatedMonthlyCard?.count ?? 0 },
          { Metric: this.incidentsSharedMonthlyCard?.label ?? 'Incidents Shared', Value: this.incidentsSharedMonthlyCard?.count ?? 0 },
          { Metric: this.totalIncidentsCard?.label ?? 'Total Incidents', Value: this.totalIncidentsCard?.count ?? 0 },
          { Metric: this.activeIncidentsCard?.label ?? 'Active Incidents', Value: this.activeIncidentsCard?.count ?? 0 },
          { Metric: 'Deleted or Expired Incidents', Value: this.latestIncidentData?.deletedIncidents ?? 0 },
          { Metric: this.totalIncidentsSharedCard?.label ?? 'Total Incidents Shared', Value: this.totalIncidentsSharedCard?.count ?? 0 },
        ]
      },
      {
        title: `Incidents Created Over Time (${incidentsCreatedDateRangeStr})`,
        data: (this.incidentsCreatedChart?.incidentsCreatedGraph?.data || []).map(d => ({
          Month: d.name, 'Incidents Created': d.actual ?? '', 'Predicted': d.predicted ?? ''
        }))
      },
      {
        title: `Total Active Incidents Over Time (${incidentsCreatedDateRangeStr})`,
        data: (this.incidentsCreatedChart?.totalActiveIncidentsGraph?.data || []).map(d => ({
          Month: d.name, 'Active Incidents': d.actual ?? '', 'Predicted': d.predicted ?? ''
        }))
      },
      {
        title: `Total Storage Over Time (${incidentsCreatedDateRangeStr})`,
        data: (this.incidentsCreatedChart?.totalStorageGraph?.data || []).map(d => ({
          Month: d.name, 'Total Storage (GB)': d.actual ?? '', 'Predicted (GB)': d.predicted ?? ''
        }))
      },
      {
        title: `Monthly Storage (${incidentsCreatedDateRangeStr})`,
        data: (this.incidentsCreatedChart?.monthlyStorageGraph?.data || []).map(d => ({
          Month: d.name, 'Monthly Storage (GB)': d.actual ?? '', 'Predicted (GB)': d.predicted ?? ''
        }))
      },
      {
        title: `Incidents Shared Over Time (${incidentsSharedDateRangeStr})`,
        data: (this.incidentsSharedChart?.graphPair?.leftGraph?.data || []).map(d => ({
          Month: d.name, 'Incidents Shared': d.actual ?? ''
        }))
      },
      {
        title: `Upload Statuses (${this.uploadStatusesChart?.startDate?.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) ?? currentMonth})`,
        data: (this.uploadStatusesChart?.pieData || []).map(d => ({
          Status: d.name, Count: d.value
        }))
      },
      {
        title: `Upload Retries (${currentMonth})`,
        data: [
          { Metric: this.retryAttemptsCard?.label ?? 'Retry Attempts in the Current Month', Value: this.retryAttemptsCard?.count ?? 0 },
          { Metric: this.successfulRetriesCard?.label ?? 'Successful Retries', Value: this.successfulRetriesCard?.count ?? 0 },
          { Metric: this.failedRetriesCard?.label ?? 'Failed Retries', Value: this.failedRetriesCard?.count ?? 0 },
        ]
      },
      {
        title: `Downloads (${this.downloadsChart?.startDate?.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) ?? currentMonth})`,
        data: [
          { Metric: this.downloadsChart?.downloadAllCard?.label ?? 'Download All', Value: this.downloadsChart?.downloadAllCard?.count ?? 0 },
          { Metric: this.downloadsChart?.downloadClipCard?.label ?? 'Individual Clips', Value: this.downloadsChart?.downloadClipCard?.count ?? 0 },
          { Metric: this.downloadsChart?.downloadClipAudioCard?.label ?? 'Audio Files', Value: this.downloadsChart?.downloadClipAudioCard?.count ?? 0 },
        ]
      },
      {
        title: `Live Video Streaming (${currentMonth})`,
        data: [
          { Metric: this.yearlyStreamingAllowanceCard?.label ?? 'Total Live Video Minutes (Yearly Allowance)', Value: this.yearlyStreamingAllowanceCard?.count ?? 0 },
          { Metric: this.monthlyStreamingCard?.label ?? 'Live Video Watched in Minutes (Current Month)', Value: this.monthlyStreamingCard?.count ?? 0 },
        ]
      },
      {
        title: `Streaming Over Time (${streamingDateRangeStr})`,
        data: (this.streamingGraph?.graphPair?.leftGraph?.data || []).map(d => ({
          Month: d.name, 'Streaming Minutes': d.actual ?? ''
        }))
      },
      {
        title: `Redaction (${currentMonth})`,
        data: [
          { Metric: this.monthlyRedactionRequestsCard?.label ?? 'Redaction Requests', Value: this.monthlyRedactionRequestsCard?.count ?? 0 },
          { Metric: this.monthlyRedactionMinutesCard?.label ?? 'Redacted Videos (Mins)', Value: this.monthlyRedactionMinutesCard?.count ?? 0 },
          { Metric: this.monthlyAudioRedactionRequestsCard?.label ?? 'Redacted Audio Requests', Value: this.monthlyAudioRedactionRequestsCard?.count ?? 0 },
        ]
      },
      {
        title: `Video Redaction Requests Over Time (${videoRedactionDateRangeStr})`,
        data: (this.redactionGraph?.videoRedactionGraph?.data || []).map(d => ({
          Month: d.name, 'Video Redaction Requests': d.actual ?? ''
        }))
      },
      {
        title: `Audio Redaction Requests Over Time (${videoRedactionDateRangeStr})`,
        data: (this.redactionGraph?.audioRedactionGraph?.data || []).map(d => ({
          Month: d.name, 'Audio Redaction Requests': d.actual ?? ''
        }))
      },
      {
        title: 'Users',
        data: [
          { Metric: 'Currently Logged In Users', Value: this.loggedInUsersCount ?? 0 },
          { Metric: 'User Groups', Value: this.totalUserGroupCount ?? 0 },
          ...(this.usersData ? Object.entries(this.usersData).flatMap(([group, items]) =>
            items.map(item => ({ Metric: `${group} - ${item.name}`, Value: item.value }))
          ) : [])
        ]
      },
    ];

    const filteredTables = tables.filter(t => t.data && t.data.length > 0);

    this.exportService.downloadMultiTableCSV(
      `Customer Usage - ${customer.name} - ${new Date().toLocaleDateString('en-GB')}`,
      filteredTables
    );
    this.auditLogService.logEvt$({
      fieldName: null,
      type: "User",
      previousValue: null,
      newValue: `Exported ${customer.name} usage to CSV`,
      action: "Export",
      trackingDescription: "Export Customer Usage to CSV",
    }).subscribe();
  }
}
